import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { theme } from "@/config/portfolio.config";
import ThemeInjector from "@/components/ThemeInjector";
import PWAInstallPrompt from "@/components/pwa/PWAInstallPrompt";
import PWAUpdatePrompt from "@/components/pwa/PWAUpdatePrompt";

// Sell is the main landing page — loaded eagerly
import Sell from "./pages/Sell";

// Lazy load secondary pages
const Index = lazy(() => import("./pages/Index"));
const Projects = lazy(() => import("./pages/Projects"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Legal = lazy(() => import("./pages/Legal"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Blog = lazy(() => import("./pages/Blog"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-muted-foreground text-sm">Chargement...</span>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme={theme.defaultTheme} enableSystem>
      <LanguageProvider>
        <TooltipProvider>
          <ThemeInjector />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Sell page = homepage */}
              <Route path="/" element={<Sell />} />
              <Route path="/sell" element={<Sell />} />
              <Route 
                path="/cv" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Index />
                  </Suspense>
                } 
              />
              <Route 
                path="/projects" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Projects />
                  </Suspense>
                } 
              />
              <Route 
                path="/payment-success" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <PaymentSuccess />
                  </Suspense>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Admin />
                  </Suspense>
                } 
              />
              <Route 
                path="/admin/login" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminLogin />
                  </Suspense>
                } 
              />
              <Route 
                path="/legal" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Legal />
                  </Suspense>
                } 
              />
              <Route 
                path="/blog" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Blog />
                  </Suspense>
                } 
              />
              <Route 
                path="*" 
                element={
                  <Suspense fallback={<PageLoader />}>
                    <NotFound />
                  </Suspense>
                } 
              />
            </Routes>
          </BrowserRouter>
          <PWAInstallPrompt />
          <PWAUpdatePrompt />
        </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
